import { Close } from '@carbon/react/icons';
import { TextInput, Button, ComboBox } from '@carbon/react';
import { Header, Column } from '@tanstack/react-table';

import styles from './column-filter.module.scss';
import { CSSProperties, useEffect, useState } from 'react';

/**
 * TData : The type of the data in the table(Each row type). Used interally by Tanstack table.
 * TValue : The type of the value in the column. Used interally by Tanstack table
 */
interface ColumnFilterProps<TData, TValue> {
  headers: Header<TData, TValue>[];
  pinStyleGenerator: (column: Column<TData>) => CSSProperties;
}

export function ColumnFilter<TData, TValue>({
  headers,
  pinStyleGenerator,
}: ColumnFilterProps<TData, TValue>) {
  return (
    <>
      {headers.map((header) => (
        <FilterInput
          key={header.id}
          header={header}
          pinStyleGenerator={pinStyleGenerator}
        ></FilterInput>
      ))}
    </>
  );
}

export interface FilterInputProps<TData, TValue> {
  header: Header<TData, TValue>;
  pinStyleGenerator: (column: Column<TData>) => CSSProperties;
}

export function FilterInput<TData, TValue>({
  header,
  pinStyleGenerator,
}: FilterInputProps<TData, TValue>) {
  const column = header.column;

  return (
    <td
      key={header.id}
      className={styles.filterRow}
      style={{
        ...pinStyleGenerator(header.column),
        width: header.getSize(),
      }}
    >
      {!header.isPlaceholder && header.column.getCanFilter() ? (
        <div className={styles.filtersContainer}>
          {header.column.columnDef.meta?.filterVariant === 'dropdown' ? (
            <DropdownFilterInput
              column={column}
              table={header.getContext().table}
            ></DropdownFilterInput>
          ) : (
            <TextFilterInput column={column}></TextFilterInput>
          )}
        </div>
      ) : null}
    </td>
  );
}

export function TextFilterInput<TValue, TData>({
  column,
}: {
  column: Column<TData, TValue>;
}) {
  const [filter, setFilter] = useState<string>(
    (column.getFilterValue() as string) || ''
  );

  const columnFilterValue = column.getFilterValue() as string | undefined;
  useEffect(() => {
    if (columnFilterValue !== undefined && columnFilterValue !== filter) {
      setFilter(columnFilterValue);
    }
  }, [columnFilterValue, filter]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilter(value);

    column.setFilterValue(value || undefined);
  };

  const handleClear = () => {
    setFilter('');
    column.setFilterValue(undefined);
  };

  return (
    <>
      <TextInput
        id={column.id}
        value={filter}
        labelText=""
        placeholder={`Filter by ${column.columnDef.header?.toString()}`}
        className={styles.text}
        onChange={handleChange}
        autoComplete="off"
      />
      <Button onClick={handleClear} kind="ghost" size="sm">
        <Close />
      </Button>
    </>
  );
}

interface DropdownFilterInputProps<TValue, TData> {
  column: Column<TData, TValue>;
  table: any;
}

// Export for testing
export function DropdownFilterInput<TValue, TData>({
  column,
  table,
}: DropdownFilterInputProps<TValue, TData>) {
  // Use a single source of truth for the filter value
  const [inputValue, setInputValue] = useState<string>(
    (column.getFilterValue() as string) || ''
  );

  // Update when external filter value changes
  const columnFilterValue = column.getFilterValue() as string | undefined;
  useEffect(() => {
    if (columnFilterValue !== undefined && columnFilterValue !== inputValue) {
      setInputValue(columnFilterValue);
    }
  }, [columnFilterValue, inputValue]);

  const [allOptions, setAllOptions] = useState<
    { id: string; label: string; count: number }[]
  >([]);
  const [filteredOptions, setFilteredOptions] = useState<
    { id: string; label: string; count: number }[]
  >([]);

  // Extract unique values for the dropdown options with counts
  useEffect(() => {
    const data = table.options.data;
    if (data && data.length > 0) {
      // Get all values from the column
      const allValues = data.map((row: any) => row[column.id]).filter(Boolean);

      // Count occurrences of each value
      const valueCounts: Record<string, number> = {};
      allValues.forEach((value: any) => {
        const strValue = String(value);
        if (valueCounts[strValue]) {
          valueCounts[strValue]++;
        } else {
          valueCounts[strValue] = 1;
        }
      });

      // Create unique options with counts
      const uniqueOptions = Object.keys(valueCounts).map((value) => ({
        id: value,
        // Include count in the label
        label: `${value} (${valueCounts[value]})`,
        count: valueCounts[value],
      }));

      // Sort by label alphabetically
      uniqueOptions.sort((a, b) => a.id.localeCompare(b.id));

      setAllOptions(uniqueOptions);
      setFilteredOptions(uniqueOptions);
    }
  }, [table.options.data, column.id]);

  // Helper function to update filtered options based on input value
  const updateFilteredOptions = (value: string) => {
    if (value) {
      const filtered = allOptions.filter((option) =>
        option.id.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(allOptions);
    }
  };

  // Helper function to safely extract value from either string or event
  const extractInputValue = (
    input: string | React.ChangeEvent<HTMLInputElement>
  ): string => {
    // If input is a string, use it directly
    if (typeof input === 'string') {
      return input;
    }
    // Otherwise safely access event.target.value
    return input?.target?.value || '';
  };

  // Handle selection of an item from dropdown
  const handleChange = ({
    selectedItem,
  }: {
    selectedItem?: { id: string; label: string; count: number };
  }) => {
    const value = selectedItem?.id || '';
    setInputValue(value);
    updateFilteredOptions(value);
    column.setFilterValue(value || undefined);
  };

  // Handle input changes (typing in the field)
  const handleInputChange = (
    input: string | React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = extractInputValue(input);
    setInputValue(value);
    updateFilteredOptions(value);
  };

  return (
    <>
      <div className={styles.dropdown}>
        <ComboBox
          id={column.id}
          items={filteredOptions}
          placeholder={`Filter by ${column.columnDef.header?.toString()}`}
          titleText=""
          selectedItem={
            inputValue
              ? allOptions.find((opt) => opt.id === inputValue) || null
              : null
          }
          onChange={(data: {
            selectedItem?: { id: string; label: string; count: number } | null;
          }) => {
            handleChange({ selectedItem: data.selectedItem || undefined });
          }}
          onInputChange={(inputValue?: string) => {
            if (inputValue !== undefined) {
              handleInputChange(inputValue);
            }
          }}
          className={styles.comboBox}
          size="sm"
          itemToString={(item) => (item ? item.id : '')}
          itemToElement={(item) => (item ? <div>{item.label}</div> : null)}
        />
      </div>
    </>
  );
}
